import React, { useId, useState } from 'react';
import Button from '../../../../shared/components/Button/Button';
import Card from '../../../../shared/components/Card/Card';
import Checkbox from '../../../../shared/components/Checkbox/Checkbox';
import Chip from '../../../../shared/components/Chip/Chip';
import Drawer from '../../../../shared/components/Drawer/Drawer';
import EmptyState from '../../../../shared/components/EmptyState/EmptyState';
import Input from '../../../../shared/components/Input/Input';
import Select from '../../../../shared/components/Select/Select';
import Typography from '../../../../shared/components/Typography/Typography';
import { slugify } from '../wiki.constants';
import { ArticleIcon, FolderIcon } from './WikiIcons';
import useMediaQuery from '../../../../hooks/useMediaQuery';

const iconOptions = [
  { value: 'folder', label: 'Dossier' },
  { value: 'article', label: 'Article' },
  { value: 'book', label: 'Référentiel' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'tool', label: 'Technique' },
];

const buildCategoryTree = (rows = []) => {
  const bySlug = new Map(rows.map((category) => [category.slug, { ...category, children: [] }]));
  const roots = [];

  bySlug.forEach((category) => {
    if (category.parent && bySlug.has(category.parent)) {
      bySlug.get(category.parent).children.push(category);
      return;
    }
    roots.push(category);
  });

  const sortTree = (nodes) => nodes
    .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0) || String(a.name || '').localeCompare(String(b.name || '')))
    .map((node) => ({ ...node, children: sortTree(node.children) }));

  return sortTree(roots);
};

function WikiAdminCategoriesPage({
  categoryDraft,
  categoryRows,
  categorySaving,
  navigate,
  onDraftChange,
  onEditCategory,
  onResetDraft,
  onSaveCategory,
  wikiCategories,
}) {
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const compactLayout = useMediaQuery('(max-width: 1100px)');
  const formDrawerTitleId = useId();
  const categoryTree = buildCategoryTree(categoryRows);
  const editCategory = (category) => {
    onEditCategory(category);
    if (compactLayout) {
      setFormDrawerOpen(true);
    }
  };
  const createCategory = () => {
    onResetDraft();
    setFormDrawerOpen(true);
  };
  const changeActiveStatus = (checked) => {
    if (!checked && categoryDraft.isActive && !window.confirm('Desactiver cette categorie ? Elle ne sera plus visible dans la navigation documentaire.')) {
      return;
    }
    onDraftChange('isActive', checked);
  };
  const renderCategoryNode = (category, depth = 0) => (
    <div key={category.slug} className={`wiki-category-tree-node depth-${Math.min(depth, 4)}`}>
      <Card compact interactive selected={categoryDraft.id === category.id} onClick={() => editCategory(category)}>
        <div className="wiki-category-tree-row">
          <span className="wiki-category-icon"><FolderIcon fontSize="small" /></span>
          <span>
            <strong>{category.name}</strong>
            <small>{category.description || category.slug}</small>
          </span>
          <div className="wiki-category-tree-badges">
            <Chip size="small" variant={category.isActive ? 'success' : 'neutral'}>
              {category.isActive ? 'Active' : 'Inactive'}
            </Chip>
            <Chip size="small" variant="info">{category.articleCount || 0} article(s)</Chip>
            <Chip size="small" variant="neutral">Ordre {category.displayOrder || 0}</Chip>
          </div>
        </div>
      </Card>
      {category.children?.map((child) => renderCategoryNode(child, depth + 1))}
    </div>
  );
  const renderCategoryForm = (drawer = false) => (
    <div className={drawer ? 'wiki-editor-settings wiki-editor-settings--drawer' : 'wiki-editor-settings'}>
      <Typography variant="h6">{categoryDraft.id ? 'Modifier la categorie' : 'Nouvelle categorie'}</Typography>
      <Input fullWidth label="Nom" value={categoryDraft.name} onChange={(event) => onDraftChange('name', event.target.value)} />
      <Input fullWidth label="Slug" value={categoryDraft.slug} onChange={(event) => onDraftChange('slug', event.target.value)} placeholder={slugify(categoryDraft.name || 'categorie')} />
      <Select
        fullWidth
        label="Parent"
        placeholder="Aucun"
        value={categoryDraft.parent}
        onChange={(event) => onDraftChange('parent', event.target.value)}
        options={wikiCategories
          .filter((category) => category.id !== categoryDraft.id)
          .map((category) => ({ value: category.slug, label: category.name }))}
      />
      <Input fullWidth label="Description" value={categoryDraft.description} onChange={(event) => onDraftChange('description', event.target.value)} multiline rows={3} />
      <div className="wiki-icon-preview">
        <span className="wiki-category-icon"><FolderIcon fontSize="small" /></span>
        <span>{iconOptions.find((option) => option.value === categoryDraft.icon)?.label || categoryDraft.icon || 'Dossier'}</span>
      </div>
      <Select
        fullWidth
        label="Icone"
        value={categoryDraft.icon}
        onChange={(event) => onDraftChange('icon', event.target.value)}
        options={iconOptions}
      />
      <Input fullWidth label="Ordre" type="number" value={categoryDraft.displayOrder} onChange={(event) => onDraftChange('displayOrder', event.target.value)} />
      <Checkbox
        checked={categoryDraft.isActive}
        label="Categorie active"
        helperText="Les categories inactives disparaissent de la navigation lecteur."
        onChange={(event) => changeActiveStatus(event.target.checked)}
      />
      <div className="wiki-editor-actions">
        <Button variant="tertiary" onClick={onResetDraft}>Nouveau</Button>
        <Button variant="primary" disabled={!categoryDraft.name || categorySaving} loading={categorySaving} onClick={onSaveCategory}>
          {categorySaving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="wiki-content-stack">
      <div className="wiki-section-header">
        <Typography variant="h4">Categories documentaires</Typography>
        <div className="wiki-row-actions">
          <Button variant="secondary" iconStart={<ArticleIcon fontSize="small" />} onClick={() => navigate('/Wiki/admin')}>
            Articles
          </Button>
          <Button className="wiki-category-new-button" variant="primary" onClick={createCategory}>
            Nouvelle categorie
          </Button>
        </div>
      </div>
      <div className="wiki-editor-layout wiki-category-layout">
        <section className="wiki-editor-main">
          <div className="wiki-category-tree">
            <div className="wiki-section-header">
              <Typography variant="h6">Arborescence</Typography>
              <span className="wiki-search-count">{categoryRows.length} categorie(s)</span>
            </div>
            {categoryTree.length ? categoryTree.map((category) => renderCategoryNode(category)) : (
              <EmptyState title="Aucune categorie">
                Creez une premiere categorie documentaire.
              </EmptyState>
            )}
          </div>
        </section>

        <aside className="wiki-category-form-panel">{renderCategoryForm()}</aside>
      </div>
      <Drawer
        labelledBy={formDrawerTitleId}
        open={formDrawerOpen}
        placement="right"
        title={categoryDraft.id ? 'Modifier la categorie' : 'Nouvelle categorie'}
        onClose={() => setFormDrawerOpen(false)}
      >
        {renderCategoryForm(true)}
      </Drawer>
    </div>
  );
}

export default WikiAdminCategoriesPage;
